import React, { useState } from 'react';
import { useScrollToContact } from '../hooks/useScrollToContact';

// Gemeinsame Bausteine des Redesigns: ruhige Linien, viel Weiß, Blau nur für Aktionen.

export const containerClass = 'mx-auto max-w-screen-xl px-5 lg:px-12';

// Linker Innenabstand für 50/50-Sektionen, damit der Text auf breiten Screens am Container ausgerichtet bleibt
export const splitTextPaddingClass = 'px-5 lg:pl-[max(3rem,calc((100vw-80rem)/2+3rem))] lg:pr-12';

export const sectionTitleClass =
  'text-[32px] font-extrabold leading-[1.05] tracking-[-0.02em] lg:text-5xl lg:leading-none lg:tracking-[-0.025em]';

export const leadClass = 'text-base leading-normal text-gray-600 lg:text-[17px]';

// Primäre Aktion: mobil volle Breite mit Pfeil rechts, ab Desktop kompakt
export const ctaClass =
  'flex w-full items-center justify-between gap-3 min-h-[52px] px-4 py-3 bg-blue-600 text-[15px] font-semibold leading-snug text-white transition-colors hover:bg-blue-700 hover:text-white lg:inline-flex lg:w-auto lg:justify-start lg:gap-3.5 lg:min-h-[48px] lg:px-5 lg:py-0 disabled:cursor-not-allowed disabled:bg-gray-400';

export const labelClass = 'mb-1.5 block text-[13px] font-semibold text-gray-700';

export const inputClass = (hasError?: boolean) =>
  `block w-full border bg-white px-3.5 text-base text-gray-900 placeholder:text-gray-400 outline-none transition-shadow focus:ring-2 focus:ring-offset-0 lg:text-[15px] ${
    hasError ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : 'border-gray-300 focus:border-blue-600 focus:ring-blue-200'
  }`;

export const Eyebrow: React.FC<{ children: React.ReactNode; dark?: boolean; className?: string }> = ({
  children,
  dark = false,
  className = '',
}) => (
  <p
    className={`mb-3 text-xs font-semibold uppercase tracking-[0.1em] lg:mb-3.5 lg:text-[13px] ${
      dark ? 'text-blue-300' : 'text-blue-600'
    } ${className}`}
  >
    {children}
  </p>
);

export const Arrow: React.FC = () => (
  <span aria-hidden="true" className="flex-none">
    →
  </span>
);

export const ArrowDownIcon: React.FC = () => (
  <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M12 5v14M5 12l7 7 7-7" />
  </svg>
);

export const CheckIcon: React.FC = () => (
  <svg
    aria-hidden="true"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="mt-px flex-none text-blue-600"
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

export const Spinner: React.FC = () => (
  <svg aria-hidden="true" className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

// Link zum Kontaktformular – scrollt sanft und fokussiert das erste Feld
export const ContactCta: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => {
  const scrollToContact = useScrollToContact();
  return (
    <a href="/#kontakt" onClick={scrollToContact} className={`${ctaClass} ${className}`}>
      {children} <Arrow />
    </a>
  );
};

export const FormAlert: React.FC<{ type: 'success' | 'error'; children: React.ReactNode; className?: string }> = ({
  type,
  children,
  className = '',
}) => (
  <div
    role={type === 'error' ? 'alert' : 'status'}
    className={`border-l-2 px-4 py-3 text-sm font-medium leading-normal ${
      type === 'success' ? 'border-green-600 bg-green-50 text-green-800' : 'border-red-600 bg-red-50 text-red-800'
    } ${className}`}
  >
    {children}
  </div>
);

interface DisclosureProps {
  // Feste ID statt useId, damit vorgerendertes HTML und Hydrierung übereinstimmen
  id: string;
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  size?: 'md' | 'lg';
  buttonClassName?: string;
  panelClassName?: string;
}

// Auf-/Zuklapp-Element mit +/−. Der Inhalt bleibt im DOM (hidden), damit er vorgerendert und indexiert wird.
export const Disclosure: React.FC<DisclosureProps> = ({
  id,
  title,
  children,
  defaultOpen = false,
  size = 'md',
  buttonClassName = '',
  panelClassName = '',
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = `${id}-inhalt`;

  const sizeClass =
    size === 'lg'
      ? 'min-h-[56px] text-[17px] lg:min-h-[60px] lg:text-[19px]'
      : 'min-h-[48px] text-base';

  return (
    <>
      <h3>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((o) => !o)}
          className={`flex w-full items-center justify-between gap-3 text-left font-semibold text-gray-900 ${sizeClass} ${buttonClassName}`}
        >
          {title}
          <span aria-hidden="true" className={`flex-none leading-none text-blue-600 ${size === 'lg' ? 'text-2xl lg:text-[26px]' : 'text-[22px]'}`}>
            {open ? '−' : '+'}
          </span>
        </button>
      </h3>
      {/* Äußeres div trägt nur "hidden", damit Display-Klassen im Panel es nicht überschreiben */}
      <div id={panelId} hidden={!open}>
        <div className={panelClassName}>{children}</div>
      </div>
    </>
  );
};
